import { useCallback, useEffect, useRef, useState } from 'react';
import getClients from '../../api/clients/getClients';
import useDebounce from '../../hooks/useDebounce';
import Select from '../Select/Select';

const mergeUniqueClients = (prev, next) => {
  const seen = new Set(prev.map((item) => item.value));
  const filtered = next.filter((item) => {
    if (seen.has(item.value)) return false;
    seen.add(item.value);
    return true;
  });
  return [...prev, ...filtered];
};

// ИИН в лейбле обязателен: полных тёзок в базе хватает, без него в списке
// несколько одинаковых строк и выбрать нужную невозможно.
const toOption = (item) => ({
  value: item.id,
  label: `${item.name} ${item.surname}` + (item.iin ? ` (ИИН: ${item.iin})` : ''),
  client: item,
});

const SelectClients = ({ handleSetSelect, handleSetState, style, excludeIds, ...props }) => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const debouncedSearch = useDebounce(search, 500);

  // Флаг в ref, а не только в state: подгрузка следующей страницы дёргается из
  // IntersectionObserver сразу после ответа, когда setLoading ещё не применился.
  const loadingRef = useRef(false);
  // Номер запроса: ответ на устаревший (пока летел — сменился поиск) выбрасываем,
  // иначе к найденным клиентам подклеится страница из прошлого запроса.
  const seqRef = useRef(0);

  const userId = new URLSearchParams(window.location.search).get('userId');

  const load = useCallback(
    (pageToLoad, term) => {
      const seq = ++seqRef.current;
      loadingRef.current = true;
      setLoading(true);

      // Пагинация должна идти с тем же фильтром, что и поиск, иначе к найденным
      // клиентам подклеиваются страницы всего справочника.
      const params = { page: pageToLoad };
      // Цифры — это ИИН или телефон: их бэк ищет параметром all, а fio смотрит
      // только на имя и фамилию (поэтому по ИИН приходило «No options»).
      if (term && /^[\d\s+()-]+$/.test(term)) params.all = term.replace(/[^\d]/g, '');
      else if (term) params.fio = term;
      else if (userId) params.id = userId;

      getClients(params)
        .then((res) => {
          if (seq !== seqRef.current) return;
          const clients = (res?.data?.users || []).map(toOption);
          setData((prev) => (pageToLoad === 1 ? clients : mergeUniqueClients(prev, clients)));
          setTotalPages(res?.data?.pagination?.totalPages || 1);
          setPage(pageToLoad);
        })
        .finally(() => {
          if (seq !== seqRef.current) return;
          loadingRef.current = false;
          setLoading(false);
        });
    },
    [userId]
  );

  // Новый запрос — всегда с первой страницы. Пустая строка тоже считается
  // сменой запроса: очистили поиск — вернули полный список.
  // Стартовое значение '' — чтобы список не грузился на маунте: первый запрос
  // уходит по открытию меню, как и раньше.
  const lastLoadedTerm = useRef('');
  useEffect(() => {
    if (lastLoadedTerm.current === debouncedSearch) return;
    lastLoadedTerm.current = debouncedSearch;
    load(1, debouncedSearch);
  }, [debouncedSearch, load]);

  const onMenuOpen = useCallback(() => {
    if (data.length === 0) load(1, debouncedSearch);
  }, [data.length, debouncedSearch, load]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || page >= totalPages) return;
    load(page + 1, debouncedSearch);
  }, [load, page, totalPages, debouncedSearch]);

  // excludeIds отсекает клиентов, которые покупателем в этой сделке быть не могут
  // (продавец комиссионного авто сам у себя не покупает).
  const options = data.filter((item) => !excludeIds?.includes(item.value));

  return (
    <Select
      handleSetSelect={(val, meta) => {
        // Сброс строки при закрытии меню — не поиск, перезапрашивать не надо.
        if (!meta || meta.action === 'input-change') setSearch(val);
        if (handleSetSelect) handleSetSelect(val, meta);
      }}
      handleSetState={handleSetState}
      options={options}
      title={'Клиент'}
      placeholder={'Выберите из списка'}
      onFocus={loadMore}
      onMenuOpen={onMenuOpen}
      style={style}
      isSearchable
      isLoading={loading}
      {...props}
    />
  );
};

export default SelectClients;
